import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineErrorOutline,
  MdOutlineEventBusy,
  MdOutlineHourglassEmpty,
  MdOutlinePayments,
} from "react-icons/md";

import { CustomButton } from "../../atoms/CustomButton";
import { Spinner } from "../../atoms/Spinner";
import type { AppointmentPayment } from "../../../hooks/agenda";
import {
  BookedAppointment,
  PaymentReturnState,
  paymentReturnOf,
  useConfirmBookingPayment,
} from "../../../hooks/agenda/publicBooking";
import { useBookingMoment } from "../customRegister/components/BookingSlotPicker";
import { paymentPrice } from "../dashboard/agenda/agendaPayment";

const K = "bookingTunnel.payment";
const AUTO_CHECK_EVERY_MS = 10_000;
const AUTO_CHECK_FOR_MS = 120_000;

/** What a booking still owes, or that it is paid, under its confirmation. */
export const BookingPaymentState: React.FC<{
  payment: AppointmentPayment;
  link?: string | null;
}> = ({ payment, link }) => {
  const { t } = useTranslation("clientView");
  const { lang } = useBookingMoment();
  const price = paymentPrice(payment, lang);

  if (payment.status === "paid") {
    return (
      <p className="text-center text-sm text-gray-600">
        {price ? t(`${K}.paid`, { price }) : t(`${K}.paidNoAmount`)}
      </p>
    );
  }
  return (
    <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <p className="flex items-baseline justify-between gap-3 text-sm">
        <span className="font-medium text-amber-800">{t(`${K}.toPay`)}</span>
        <span className="font-semibold tabular-nums text-gray-900">
          {price}
        </span>
      </p>
      {link ? (
        <CustomButton
          href={link}
          variant="primary"
          title={t(`${K}.payNow`) as string}
          className="w-full"
        />
      ) : (
        <p className="text-sm text-gray-700">{t(`${K}.noLink`)}</p>
      )}
    </div>
  );
};

export interface PaymentReturn {
  id: string;
  userId: string;
  sig: string;
}

type Outcome =
  | { kind: "checking" }
  | { kind: "invalid" }
  | { kind: "failed" }
  | {
      kind: Exclude<PaymentReturnState, "PAYMENT_DUE">;
      appointment: BookedAppointment | null;
      link: string | null;
    };

const StatusHeader: React.FC<{
  icon: React.ReactNode;
  tone: "amber" | "red" | "gray";
  title: string;
  text?: string;
}> = ({ icon, tone, title, text }) => (
  <div className="text-center">
    <div
      className={
        tone === "amber"
          ? "mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-600"
          : tone === "red"
          ? "mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600"
          : "mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-500"
      }
    >
      {icon}
    </div>
    <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
    {text && <p className="mt-1 text-sm text-gray-600">{text}</p>}
  </div>
);

const useMinutesLeft = (until?: number | null) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!until) return;
    const id = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, [until]);
  return until ? Math.max(0, Math.ceil((until - now) / 60_000)) : 0;
};

interface BookingPaymentReturnProps {
  entityId: string;
  typeId: string;
  paymentReturn: PaymentReturn;
  /** Confirmed, or booked and still to pay: the tunnel shows its confirmation. */
  onBooked: (appointment: BookedAppointment) => void;
  onPickAnother: () => void;
  /** Shown while the booking is not settled: the client sent by a sign-up form may end it without one. */
  finishWithout?: React.ReactNode;
}

/** The client back from the payment page: the booking is confirmed, or the reason it is not yet. */
export const BookingPaymentReturn: React.FC<BookingPaymentReturnProps> = ({
  entityId,
  typeId,
  paymentReturn,
  onBooked,
  onPickAnother,
  finishWithout,
}) => {
  const { t } = useTranslation("clientView");
  const { at } = useBookingMoment();
  const { mutate } = useConfirmBookingPayment(entityId, typeId);
  const [outcome, setOutcome] = useState<Outcome>({ kind: "checking" });
  const [asking, setAsking] = useState(false);
  const [stillPending, setStillPending] = useState(false);
  const inFlight = useRef(false);

  /** `first` shows the wait, `manual` answers the client's click, `auto` checks quietly and keeps the page on a failure. */
  const check = useCallback(
    (mode: "first" | "manual" | "auto" = "first") => {
      if (inFlight.current) return;
      inFlight.current = true;
      if (mode === "first") setOutcome({ kind: "checking" });
      if (mode === "manual") {
        setAsking(true);
        setStillPending(false);
      }
      mutate(paymentReturn, {
        onSuccess: onBooked,
        onError: (error) => {
          const answer = paymentReturnOf(error);
          if (answer?.state === "PAYMENT_DUE" && answer.answer.appointment) {
            onBooked({
              ...answer.answer.appointment,
              payment_link: answer.answer.payment_link,
            });
          } else if (answer && answer.state !== "PAYMENT_DUE") {
            setOutcome({
              kind: answer.state,
              appointment: answer.answer.appointment,
              link: answer.answer.payment_link,
            });
            if (mode === "manual" && answer.state === "PAYMENT_PENDING")
              setStillPending(true);
          } else if (mode !== "auto") {
            setOutcome({
              kind: error?.response?.status === 403 ? "invalid" : "failed",
            });
          }
        },
        onSettled: () => {
          inFlight.current = false;
          if (mode === "manual") setAsking(false);
        },
      });
    },
    [mutate, paymentReturn, onBooked]
  );
  const checkRef = useRef(check);
  checkRef.current = check;

  const checked = useRef(false);
  useEffect(() => {
    if (checked.current) return;
    checked.current = true;
    check();
  }, [check]);

  const pending = outcome.kind === "PAYMENT_PENDING";
  const holdEnd = pending ? outcome.appointment?.hold_expires_at : null;
  const minutesLeft = useMinutesLeft(holdEnd);

  // The payment may land a little after the client is back: checked again every 10 s for 2 minutes.
  useEffect(() => {
    if (!pending) return;
    const started = Date.now();
    const id = window.setInterval(() => {
      if (Date.now() - started > AUTO_CHECK_FOR_MS) {
        window.clearInterval(id);
        return;
      }
      checkRef.current("auto");
    }, AUTO_CHECK_EVERY_MS);
    return () => window.clearInterval(id);
  }, [pending]);

  // Once the slot is no longer held, paying could charge for nothing: the button goes, and the real state is asked once.
  const [holdOver, setHoldOver] = useState(false);
  useEffect(() => {
    if (!holdEnd) return;
    const left = holdEnd - Date.now();
    if (left <= 0) {
      setHoldOver(true);
      return;
    }
    const id = window.setTimeout(() => {
      setHoldOver(true);
      checkRef.current("auto");
    }, left + 500);
    return () => window.clearTimeout(id);
  }, [holdEnd]);

  if (outcome.kind === "checking") {
    return (
      <div
        className="flex items-center justify-center gap-2 py-8 text-sm text-gray-600"
        translate="no"
      >
        <Spinner size="sm" />
        {t(`${K}.checking`)}
      </div>
    );
  }

  const checkAgain = (
    <CustomButton
      variant="secondary"
      title={t(`${K}.checkAgain`) as string}
      onClick={() => check("manual")}
      isLoading={asking}
      disabled={asking}
      className="w-full"
    />
  );

  if (outcome.kind === "PAYMENT_PENDING") {
    return (
      <div className="space-y-6">
        <StatusHeader
          icon={<MdOutlineHourglassEmpty size={24} aria-hidden="true" />}
          tone="amber"
          title={t(`${K}.pendingTitle`)}
          text={
            holdEnd && minutesLeft > 0 && !holdOver
              ? (t(`${K}.pendingUntil`, {
                  time: at(holdEnd).format("LT"),
                  minutes: minutesLeft,
                }) as string)
              : (t(`${K}.pendingOver`) as string)
          }
        />
        <div className="space-y-3">
          {outcome.link && !holdOver && (
            <CustomButton
              href={outcome.link}
              variant="primary"
              title={t(`${K}.pay`) as string}
              className="w-full"
            />
          )}
          {checkAgain}
          <p role="status" className="text-center text-sm text-amber-700">
            {stillPending && t(`${K}.stillPending`)}
          </p>
          {finishWithout}
        </div>
      </div>
    );
  }

  if (outcome.kind === "HOLD_EXPIRED") {
    return (
      <div className="space-y-6">
        <StatusHeader
          icon={<MdOutlineHourglassEmpty size={24} aria-hidden="true" />}
          tone="gray"
          title={t(`${K}.expiredTitle`)}
          text={t(`${K}.expiredText`) as string}
        />
        <div className="space-y-3">
          <CustomButton
            variant="primary"
            title={t(`${K}.pickAnother`) as string}
            onClick={onPickAnother}
            className="w-full"
          />
          {finishWithout}
        </div>
      </div>
    );
  }

  if (outcome.kind === "BOOKING_CANCELLED") {
    return (
      <div className="space-y-6">
        <StatusHeader
          icon={<MdOutlineEventBusy size={24} aria-hidden="true" />}
          tone="gray"
          title={t(`${K}.cancelledTitle`)}
          text={t(`${K}.cancelledText`) as string}
        />
        {finishWithout}
      </div>
    );
  }

  if (outcome.kind === "SLOT_TAKEN_AFTER_PAYMENT") {
    return (
      <div className="space-y-6">
        <StatusHeader
          icon={<MdOutlinePayments size={24} aria-hidden="true" />}
          tone="amber"
          title={t(`${K}.takenTitle`)}
          text={t(`${K}.takenText`) as string}
        />
        {finishWithout}
      </div>
    );
  }

  if (outcome.kind === "invalid") {
    return (
      <StatusHeader
        icon={<MdOutlineErrorOutline size={24} aria-hidden="true" />}
        tone="red"
        title={t(`${K}.invalidTitle`)}
        text={t(`${K}.invalidText`) as string}
      />
    );
  }

  return (
    <div className="space-y-6">
      <StatusHeader
        icon={<MdOutlineErrorOutline size={24} aria-hidden="true" />}
        tone="red"
        title={t(`${K}.failedTitle`)}
        text={t(`${K}.failedText`) as string}
      />
      <CustomButton
        variant="secondary"
        title={t(`${K}.retry`) as string}
        onClick={() => check("first")}
        className="w-full"
      />
    </div>
  );
};
